import { View } from "@novorender/api";
import { ObjectDB } from "@novorender/data-js-api";
import { HierarcicalObjectReference, ObjectData, ObjectId, SearchPattern } from "@novorender/data-js-api";
import pMap from "p-map";

import { telemetry } from "telemetry";

import { AppObjectDB } from "./appObjectDb";
import { estimateShrunkSize, IdArrayShrinker, pickBestShrink, ShrunkIdArray } from "./idArrayShrinker";
import { registerMemoryStatsProvider } from "./memoryDiagnostics";
import { appendInPlace } from "./misc";
import { isLeafNode } from "./objectPath";
import { sleep } from "./time";

const defaultCallbackInterval = 10000;

/**
 * Deliver ids to the callback in bounded slices. Cached `descendants` arrays
 * can hold millions of ids; handing them to consumers in one call invites
 * `fn(...ids)`-style argument-limit crashes and long synchronous stalls.
 */
function emitIdsInChunks(ids: ObjectId[], callback: (result: ObjectId[]) => void, chunkSize: number): void {
    if (ids.length <= chunkSize) {
        callback(ids);
        return;
    }
    for (let start = 0; start < ids.length; start += chunkSize) {
        callback(ids.slice(start, start + chunkSize));
    }
}

export async function iterateAsync<T = HierarcicalObjectReference>({
    iterator,
    count,
    abortSignal,
}: {
    iterator: AsyncIterableIterator<T>;
    count: number;
    abortSignal?: AbortSignal;
}): Promise<[T[], boolean]> {
    const values: T[] = [];
    let done = false;

    for (let i = 0; i < count; i++) {
        if (abortSignal?.aborted) {
            break;
        }

        const next = await iterator.next();

        if (next.done) {
            done = true;
            break;
        }

        values.push(next.value);
    }

    return [values, done];
}

/**
    Find objects matching the specified patterns.
    The callback gets called at the specified interval with only the new results since the last call.

    @remarks
    Some searches may return thousands of objects and take several seconds to complete.
*/
export async function searchByPatterns<T = HierarcicalObjectReference>({
    db,
    searchPatterns,
    callback,
    callbackInterval = defaultCallbackInterval,
    abortSignal,
    full,
    skipPermissionCheck,
}: {
    db: AppObjectDB;
    searchPatterns: SearchPattern[] | string;
    callback: (result: T[]) => void;
    callbackInterval?: number;
    abortSignal?: AbortSignal;
    full?: boolean;
    skipPermissionCheck?: boolean;
}): Promise<void> {
    const iterator = db.search(
        { searchPattern: searchPatterns, full, skipPermissionCheck },
        abortSignal,
    ) as AsyncIterableIterator<T>;
    let done = false;

    while (!done && !abortSignal?.aborted) {
        const [result, _done] = await iterateAsync<T>({ iterator, abortSignal, count: callbackInterval });
        done = _done;
        callback(result);
    }
}

/**
    Find objects matching the specified patterns.
    The callback gets called at the specified interval with only the new results since the last call.
    Includes the IDs of all children of parent nodes found matching the search pattern.

    @remarks
    Some searches may return thousands of objects and take several seconds to complete.
*/
export async function searchDeepByPatterns({
    db,
    searchPatterns,
    callback,
    callbackInterval = defaultCallbackInterval,
    abortSignal,
    skipPermissionCheck,
}: {
    db: AppObjectDB;
    searchPatterns: SearchPattern[] | string;
    callback: (result: ObjectId[]) => void;
    callbackInterval?: number;
    abortSignal?: AbortSignal;
    skipPermissionCheck?: boolean;
}): Promise<void> {
    const iterator = db.search({ searchPattern: searchPatterns, skipPermissionCheck }, abortSignal);
    let done = false;

    while (!done && !abortSignal?.aborted) {
        const [result, _done] = await iterateAsync({ iterator, abortSignal, count: callbackInterval });
        done = _done;
        callback(result.map((res) => res.id));
        await sleep(1);

        const cachedDescendants: ObjectId[] = [];
        const unCachedDescendants: HierarcicalObjectReference[] = [];
        for (const obj of result) {
            if (isLeafNode(obj)) {
                continue;
            } else if (obj.descendants) {
                // Append in place: repeated `concat` re-copies the accumulator
                // per node, which is quadratic on large sibling lists.
                appendInPlace(cachedDescendants, obj.descendants);
            } else {
                unCachedDescendants.push(obj);
            }
        }

        emitIdsInChunks(cachedDescendants, callback, callbackInterval);

        await pMap(
            unCachedDescendants,
            (obj) =>
                getDescendants({ db: db, parentNode: obj, skipPermissionCheck, abortSignal })
                    .then((ids) => emitIdsInChunks(ids, callback, callbackInterval))
                    .catch(() =>
                        searchByParentPath({
                            db: db,
                            abortSignal,
                            callback: (results) => callback(results.map((res) => res.id)),
                            callbackInterval: callbackInterval,
                            parentPath: obj.path,
                            skipPermissionCheck,
                        }),
                    ),
            { concurrency: 10 },
        );
    }
}

/**
    Find objects by parent path.
    The callback gets called at the specified interval with only the new results since the last call.

    @remarks
    Some searches may return thousands of objects and take several seconds to complete.
*/
export async function searchByParentPath({
    db,
    parentPath,
    callback,
    callbackInterval = defaultCallbackInterval,
    depth,
    abortSignal,
    skipPermissionCheck,
}: {
    db: AppObjectDB;
    parentPath: string;
    callback: (result: HierarcicalObjectReference[]) => void;
    callbackInterval?: number;
    depth?: number;
    abortSignal?: AbortSignal;
    skipPermissionCheck?: boolean;
}): Promise<void> {
    const allChildren = db.search({ parentPath, descentDepth: depth, skipPermissionCheck }, abortSignal);
    let done = false;

    while (!done && !abortSignal?.aborted) {
        const [result, _done] = await iterateAsync({ iterator: allChildren, count: callbackInterval, abortSignal });
        done = _done;
        callback(result);
        await sleep(1);
    }
}

/**
 * Find the first object that matches the passed in path, and load its full metadata.
 *  */
export async function searchFirstObjectAtPath({
    db,
    path,
}: {
    db: ObjectDB;
    path: string;
}): Promise<ObjectData | undefined> {
    return db
        .search({ parentPath: path, descentDepth: 0, full: true }, undefined)
        .next()
        .then((res) => (res.value ? res.value.loadMetaData() : undefined))
        .catch(() => undefined);
}

/**
 * De-duplicates concurrent `getDescendants` calls for the same node. Selection
 * changes, tree expansion and background preloads can all ask for the same
 * folder's descendants at once; without this each caller would fire its own
 * (potentially very large) request.
 *
 * Keyed by db + object **id**, not node reference, for the same reason as
 * {@link descendantsFastPathUnavailable}: the tree recreates
 * `HierarcicalObjectReference` objects constantly during loading, so a
 * reference-keyed map lets the preloader and a user-driven fetch download the
 * same (potentially tens of MB) descendants file concurrently.
 */
const inFlightDescendants = new WeakMap<AppObjectDB, Map<ObjectId, Promise<ObjectId[]>>>();

/**
 * Resolved descendants keyed by db + object id. The per-node lazy
 * `parentNode.descendants` accessor (see {@link defineLazyDescendants}) dies
 * with the node object on tree rebuilds; this cache survives them so an id is
 * never fetched twice per scene. Bounded by scene lifetime (WeakMap on db).
 *
 * Entries are stored shrunk when it pays off. Descendant ids are assigned
 * breadth-first, so a folder's subtree is a near-contiguous id range —
 * measured at ~600:1 compression via bitmap/RLE on real scenes. Small entries
 * (< {@link SHRINK_MIN_IDS}) stay as plain `Uint32Array`s: at that size the
 * shrink/expand overhead outweighs the bytes saved. Consumers get expanded
 * `Uint32Array`s (sorted, exposed as `ObjectId[]`) via {@link expandDescendants},
 * which keeps recently used expansions in a bounded hot cache.
 */
type CachedDescendants = Uint32Array | ShrunkIdArray;

const resolvedDescendants = new WeakMap<AppObjectDB, Map<ObjectId, CachedDescendants>>();

/**
 * Folder paths for resolved cache entries, kept only so memory diagnostics
 * can name the largest folders (a few hundred short strings per scene).
 */
const resolvedDescendantPaths = new WeakMap<AppObjectDB, Map<ObjectId, string>>();

/** Below this many ids an entry is stored as a raw Uint32Array. */
const SHRINK_MIN_IDS = 1024;

/**
 * Budget for the hot cache of expanded arrays, in total ids across entries
 * (512k ids = 2 MB as Uint32Array). Entry-count limits don't work here:
 * entries range from ~1k to ~400k ids, so a fixed count either starves dense
 * scenes or admits tens of MB.
 */
const HOT_CACHE_MAX_TOTAL_IDS = 512 * 1024;

interface HotDescendantsCache {
    /** Insertion order doubles as LRU order: refresh = delete + re-set. */
    entries: Map<ObjectId, Uint32Array>;
    totalIds: number;
}

const expandedHot = new WeakMap<AppObjectDB, HotDescendantsCache>();

/**
 * Store an id list in the resolved cache, shrunk when that is actually
 * smaller than the raw typed array.
 */
function packDescendants(ids: Uint32Array): CachedDescendants {
    if (ids.length < SHRINK_MIN_IDS) {
        return ids;
    }
    // Strategies operate on indexed, iterable, sorted input — a Uint32Array
    // satisfies all of that at runtime despite the number[] signature.
    const asNumbers = ids as unknown as number[];
    // Pick once: shrink() would otherwise re-run every strategy's estimateSize
    // (bitmapRLE / chunkedBitmap each scan the whole array).
    const { type, size } = pickBestShrink(asNumbers);
    if (size >= ids.byteLength) {
        return ids;
    }
    return IdArrayShrinker.shrink(asNumbers, type);
}

function putInHotCache(db: AppObjectDB, id: ObjectId, expanded: Uint32Array): void {
    let hot = expandedHot.get(db);
    if (!hot) {
        hot = { entries: new Map(), totalIds: 0 };
        expandedHot.set(db, hot);
    }
    const previous = hot.entries.get(id);
    if (previous) {
        hot.entries.delete(id);
        hot.totalIds -= previous.length;
    }
    hot.entries.set(id, expanded);
    hot.totalIds += expanded.length;

    // Evict oldest-first until within budget, but always keep the entry that
    // was just inserted (it is last in insertion order, so the size > 1 guard
    // protects it).
    while (hot.totalIds > HOT_CACHE_MAX_TOTAL_IDS && hot.entries.size > 1) {
        const [oldestId, oldest] = hot.entries.entries().next().value!;
        hot.entries.delete(oldestId);
        hot.totalIds -= oldest.length;
    }
}

/** Expand a cache entry, serving repeat access from the hot cache. */
function expandDescendants(db: AppObjectDB, id: ObjectId, entry: CachedDescendants): Uint32Array {
    if (entry instanceof Uint32Array) {
        return entry;
    }

    const hot = expandedHot.get(db);
    const cached = hot?.entries.get(id);
    if (cached) {
        // Refresh LRU position.
        hot!.entries.delete(id);
        hot!.entries.set(id, cached);
        return cached;
    }

    const expanded = IdArrayShrinker.expand(entry);
    putInHotCache(db, id, expanded);
    return expanded;
}

/**
 * Sync read of a resolved entry (expanded on demand), or `undefined` when the
 * id has not been resolved for this db. Backs the lazy `node.descendants`
 * accessor, and with it every synchronous consumer (group-state counting in
 * treeHelpers, preload-decision checks, ...).
 */
function getResolvedDescendants(db: AppObjectDB, id: ObjectId): Uint32Array | undefined {
    const entry = resolvedDescendants.get(db)?.get(id);
    return entry === undefined ? undefined : expandDescendants(db, id, entry);
}

/**
 * Expose `node.descendants` as a lazy accessor over the resolved cache
 * instead of assigning the raw array. A plain assignment pins the full id
 * list (up to MBs per folder) for the node's lifetime; the accessor pins only
 * the shrunk form, expanding through the bounded hot cache on access.
 *
 * The setter keeps plain assignments working (`appObjectDb` clears the field
 * on abort) by demoting the property back to a data property.
 */
function defineLazyDescendants(db: AppObjectDB, node: HierarcicalObjectReference): void {
    const id = node.id;
    Object.defineProperty(node, "descendants", {
        configurable: true,
        enumerable: true,
        get: () => getResolvedDescendants(db, id) as unknown as ObjectId[] | undefined,
        set(value: ObjectId[] | undefined) {
            Object.defineProperty(this, "descendants", {
                value,
                writable: true,
                configurable: true,
                enumerable: true,
            });
        },
    });
}

/**
 * Object ids whose pre-computed `descendants` file is known to be unavailable
 * (a genuine 404, not an abort), keyed per db/scene. Keyed by **id** rather than
 * node reference so the knowledge survives tree rebuilds — the tree recreates
 * `HierarcicalObjectReference` objects constantly during loading, and a
 * reference-keyed cache would forget the miss and re-probe (404) the same id
 * over and over. Populated by {@link preloadDescendantsFastPath}; consulted by
 * both the preload and the user-selection path so neither wastes a round-trip on
 * a file that isn't there.
 */
const descendantsFastPathUnavailable = new WeakMap<AppObjectDB, Set<ObjectId>>();

/**
 * Dbs whose descendant caches should show up in memory diagnostics. The caches
 * above are WeakMaps (deliberately, so they die with the scene) and WeakMaps
 * can't be enumerated — so track the keys separately via `WeakRef`s, which
 * don't extend the db's lifetime. Dead refs are pruned on each report.
 */
const diagnosticsDbs = new Set<WeakRef<AppObjectDB>>();

function trackDbForDiagnostics(db: AppObjectDB): void {
    for (const ref of diagnosticsDbs) {
        if (ref.deref() === db) {
            return;
        }
    }
    diagnosticsDbs.add(new WeakRef(db));
}

registerMemoryStatsProvider("descendantsCache", () => {
    let sceneCount = 0;
    let entryCount = 0;
    let shrunkEntryCount = 0;
    let totalIds = 0;
    let largestEntryIds = 0;
    let residentBytes = 0;
    let hotCacheEntries = 0;
    let hotCacheIds = 0;
    let fastPathUnavailableCount = 0;
    const allEntries: { id: ObjectId; path: string | undefined; idCount: number }[] = [];

    for (const ref of diagnosticsDbs) {
        const db = ref.deref();
        if (!db) {
            diagnosticsDbs.delete(ref);
            continue;
        }
        sceneCount++;

        const cache = resolvedDescendants.get(db);
        if (cache) {
            entryCount += cache.size;
            const paths = resolvedDescendantPaths.get(db);
            for (const [id, entry] of cache) {
                totalIds += entry.length;
                largestEntryIds = Math.max(largestEntryIds, entry.length);
                allEntries.push({ id, path: paths?.get(id), idCount: entry.length });
                if (entry instanceof Uint32Array) {
                    residentBytes += entry.byteLength;
                } else {
                    shrunkEntryCount++;
                    residentBytes += estimateShrunkSize(entry);
                }
            }
        }

        const hot = expandedHot.get(db);
        if (hot) {
            hotCacheEntries += hot.entries.size;
            hotCacheIds += hot.totalIds;
        }
        fastPathUnavailableCount += descendantsFastPathUnavailable.get(db)?.size ?? 0;
    }

    return {
        sceneCount,
        entryCount,
        shrunkEntryCount,
        totalIds,
        /** What the same data would cost stored raw (4 bytes per id). */
        rawBytes: totalIds * 4,
        /** Actual resident cost of the cache (shrunk + raw entries). */
        residentBytes,
        largestEntryIds,
        /** The five biggest folders by descendant count, named by path. */
        largestEntries: allEntries.sort((a, b) => b.idCount - a.idCount).slice(0, 5),
        hotCacheEntries,
        /** Resident cost of the hot cache of expanded arrays. */
        hotCacheBytes: hotCacheIds * 4,
        fastPathUnavailableCount,
    };
});

function isDescendantsFastPathUnavailable(db: AppObjectDB, id: ObjectId): boolean {
    return descendantsFastPathUnavailable.get(db)?.has(id) ?? false;
}

function markDescendantsFastPathUnavailable(db: AppObjectDB, id: ObjectId): void {
    let set = descendantsFastPathUnavailable.get(db);
    if (!set) {
        set = new Set<ObjectId>();
        descendantsFastPathUnavailable.set(db, set);
    }
    set.add(id);
}

export async function getDescendants({
    db,
    parentNode,
    abortSignal,
    skipPermissionCheck,
    fastPathOnly,
}: {
    db: AppObjectDB;
    parentNode: HierarcicalObjectReference;
    abortSignal?: AbortSignal;
    skipPermissionCheck?: boolean;
    /**
     * When `true`, resolve only via the pre-computed descendants file and reject
     * if it is missing instead of falling back to an unbounded subtree search.
     */
    fastPathOnly?: boolean;
}): Promise<ObjectId[]> {
    if (parentNode.descendants) {
        return parentNode.descendants;
    }

    trackDbForDiagnostics(db);

    const resolved = getResolvedDescendants(db, parentNode.id);
    if (resolved) {
        defineLazyDescendants(db, parentNode);
        return resolved as unknown as ObjectId[];
    }

    let inFlight = inFlightDescendants.get(db);
    if (!inFlight) {
        inFlight = new Map();
        inFlightDescendants.set(db, inFlight);
    }
    const existing = inFlight.get(parentNode.id);
    if (existing) {
        return existing;
    }

    // If a prior fast-path preload already found this id's pre-computed file
    // missing, skip re-fetching it (a guaranteed 404) and go straight to the
    // search fallback. Not applicable to fast-path-only callers (they only want
    // the file and reject when it's absent).
    const skipFastPath = !fastPathOnly && isDescendantsFastPathUnavailable(db, parentNode.id);

    const promise = db
        .descendants(parentNode, abortSignal, skipPermissionCheck, fastPathOnly, skipFastPath)
        .then((ids) => {
            // Pack into a Uint32Array (see resolvedDescendants doc). Filter in
            // the same pass — the descendants source can contain NaNs (bug).
            let count = 0;
            const packed = new Uint32Array(ids.length);
            for (let i = 0; i < ids.length; i++) {
                const id = ids[i];
                if (!isNaN(id)) {
                    packed[count++] = id;
                }
            }
            const filteredIds = count === packed.length ? packed : packed.slice(0, count);
            // Sort IDs to make lookups faster (countSelectedDescendants binary-searches
            // this). Typed-array sort is numeric by default.
            filteredIds.sort();
            let cache = resolvedDescendants.get(db);
            if (!cache) {
                cache = new Map();
                resolvedDescendants.set(db, cache);
            }
            const entry = packDescendants(filteredIds);
            cache.set(parentNode.id, entry);
            let paths = resolvedDescendantPaths.get(db);
            if (!paths) {
                paths = new Map();
                resolvedDescendantPaths.set(db, paths);
            }
            paths.set(parentNode.id, parentNode.path);
            if (entry !== filteredIds) {
                // Seed the hot cache so the callers holding the array we
                // return and the node's lazy accessor observe the same
                // instance instead of re-expanding immediately.
                putInHotCache(db, parentNode.id, filteredIds);
            }
            defineLazyDescendants(db, parentNode);
            return filteredIds as unknown as ObjectId[];
        })
        .finally(() => {
            inFlight.delete(parentNode.id);
        });

    inFlight.set(parentNode.id, promise);
    return promise;
}

/**
 * Warm the `descendants` cache for a node using only the cheap pre-computed
 * file — never the unbounded subtree search. Intended for background preloads
 * that trade completeness for cost: if the file is missing the node is marked
 * unavailable so repeated selection/expansion changes don't re-fire a doomed
 * request, and the expensive search stays reserved for a deliberate,
 * user-initiated selection.
 *
 * @returns `true` if descendants are now cached, `false` if unavailable/aborted.
 */
export async function preloadDescendantsFastPath({
    db,
    parentNode,
    abortSignal,
    skipPermissionCheck,
}: {
    db: AppObjectDB;
    parentNode: HierarcicalObjectReference;
    abortSignal?: AbortSignal;
    skipPermissionCheck?: boolean;
}): Promise<boolean> {
    if (parentNode.descendants) {
        return true;
    }
    if (isDescendantsFastPathUnavailable(db, parentNode.id)) {
        return false;
    }

    try {
        await getDescendants({ db, parentNode, abortSignal, skipPermissionCheck, fastPathOnly: true });
        return true;
    } catch {
        // Don't poison the negative cache on abort — the file may exist and just
        // wasn't fetched. A genuine miss (404 etc.) stays cached so we stop retrying.
        if (!abortSignal?.aborted) {
            markDescendantsFastPathUnavailable(db, parentNode.id);
        }
        return false;
    }
}

export function getObjectData({
    db,
    id,
    view,
}: {
    db: ObjectDB;
    id: ObjectId;
    view: View;
}): Promise<ObjectData | undefined> {
    return view.data
        ? (view.data.getObjectMetaData(id) as unknown as Promise<ObjectData>)
        : db.getObjectMetdata(id).catch(() => undefined);
}

export async function batchedPropertySearch<T = HierarcicalObjectReference>({
    property,
    value,
    transformResult,
    db,
    abortSignal,
    full,
    skipPermissionCheck,
}: {
    property: string;
    value: string[];
    transformResult?: (res: (HierarcicalObjectReference | ObjectData)[]) => T[];
    db: AppObjectDB;
    abortSignal: AbortSignal;
    full?: boolean;
    skipPermissionCheck?: boolean;
}): Promise<T[]> {
    // Special case for single ID to utilize built in ObjectDB cache.
    if (property.toLowerCase() === "id" && value.length === 1) {
        try {
            const obj = await db.getObjectMetdata(Number(value[0]), skipPermissionCheck);
            return transformResult ? transformResult([obj]) : [obj as T];
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    let result = [] as T[];

    const batchSize = 500;
    const batches = value.reduce(
        (acc, val) => {
            const lastBatch = acc.slice(-1)[0];

            if (lastBatch.length < batchSize) {
                lastBatch.push(val);
            } else {
                acc.push([val]);
            }

            return acc;
        },
        [[]] as string[][],
    );

    const concurrentRequests = 5;
    const callback = (refs: (HierarcicalObjectReference | ObjectData)[]) => {
        result = result.concat(transformResult ? transformResult(refs) : (refs as T));
    };

    for (let i = 0; i < batches.length / concurrentRequests; i++) {
        if (abortSignal.aborted) {
            return [];
        }

        await Promise.all(
            batches.slice(i * concurrentRequests, i * concurrentRequests + concurrentRequests).map((batch) => {
                return searchByPatterns({
                    db,
                    callback,
                    abortSignal,
                    full,
                    searchPatterns: [
                        {
                            property,
                            value: batch,
                            exact: true,
                        },
                    ],
                    skipPermissionCheck,
                }).catch(() => {
                    // continue
                });
            }),
        );
    }

    if (abortSignal.aborted) {
        return [];
    }

    return result;
}

export async function searchForGroups({
    db,
    searchPatterns,
    abortSignal,
    callback,
}: {
    db: AppObjectDB;
    searchPatterns: SearchPattern[];
    abortSignal: AbortSignal;
    callback: (result: ObjectId[]) => void;
}) {
    const deep = searchPatterns.every((input) => !input.exclude);
    if (deep) {
        const seenIds = new Set<ObjectId>();

        await searchDeepByPatterns({
            abortSignal,
            db,
            searchPatterns,
            callback: (result) => {
                const filtered = result.filter((id) => {
                    if (seenIds.has(id)) {
                        return false;
                    }

                    seenIds.add(id);
                    return true;
                });

                if (filtered.length) {
                    callback(filtered);
                }
            },
            skipPermissionCheck: true,
        }).catch((error) => {
            console.warn(error);
            telemetry.trackCaughtException({
                error,
                severity: "error",
                source: "utils.search.searchForGroups",
                category: "data",
            });
        });
    } else {
        await searchByPatterns({
            abortSignal,
            db,
            searchPatterns,
            callback: (result) => callback(result.map((res) => res.id)),
            skipPermissionCheck: true,
        }).catch((error) => {
            console.warn(error);
            telemetry.trackCaughtException({
                error,
                severity: "error",
                source: "utils.search.searchForGroups",
                category: "data",
            });
        });
    }

    return { deep };
}
